<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  round: number;
  matches: any[];
  actionLoading: boolean;
  isRoundActive: (round: number) => boolean;
  getCompletedMatchesCount: (round: number) => number;
  isLastRound?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'finish-round': [round: number];
}>();

const { t } = useI18n();

// Check if all matches in this round are completed
const isRoundFinished = computed(() => {
  const totalMatches = props.matches.length;
  const completedMatches = props.getCompletedMatchesCount(props.round);
  return totalMatches > 0 && completedMatches === totalMatches;
});

// Check if this round is the current active round
const isCurrentRound = computed(() => {
  return props.isRoundActive(props.round);
});

// Determine if button should be disabled
const isDisabled = computed(() => {
  return props.actionLoading || !isRoundFinished.value || !isCurrentRound.value;
});

const buttonTitle = computed(() => {
  if (!isRoundFinished.value) return t('tournament.round_finish.title.finish_all_matches_in_round');
  return props.isLastRound
    ? t('tournament.round_finish.title.finish_tournament')
    : t('tournament.round_finish.title.finish_round_and_advance');
});

const buttonLabel = computed(() => {
  if (props.actionLoading) return t('tournament.round_finish.button.processing');
  return props.isLastRound
    ? t('tournament.round_finish.button.finish_tournament')
    : t('tournament.round_finish.button.finish_round');
});

const tooltipText = computed(() => {
  if (isRoundFinished.value) {
    return props.isLastRound
      ? t('tournament.round_finish.tooltip.all_matches_finished_finish_tournament')
      : t('tournament.round_finish.tooltip.all_matches_finished_advance');
  }

  return props.isLastRound
    ? t('tournament.round_finish.tooltip.finish_all_matches_to_finish_tournament')
    : t('tournament.round_finish.tooltip.finish_all_matches_to_advance');
});

const handleFinishRound = () => {
  if (!isDisabled.value) {
    emit('finish-round', props.round);
  }
};
</script>

<template>
  <div v-if="isCurrentRound" class="round-finish-button-container">
    <button
      @click="handleFinishRound"
      class="btn btn-round-finish"
      type="button"
      :disabled="isDisabled"
      :title="buttonTitle"
    >
      <span v-if="actionLoading" class="loading-spinner">⏳</span>
      {{ buttonLabel }}
    </button>
    <div class="round-finish-tooltip">
      <i class="bi bi-info-circle"></i>
      <span>{{ tooltipText }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.round-finish-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
}

.btn-round-finish {
  background-color: var(--icon-bg);
  color: var(--icon-text);
  border: 2px solid var(--border-color);
  font-weight: bold;
  transition: var(--transition-fast);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);

  &:hover:not(:disabled) {
    background-color: var(--hover-bg);
    border-color: var(--hover-bg);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px var(--shadow-light);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px var(--shadow-light);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

.round-finish-tooltip {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  text-align: center;
  max-width: 300px;

  i {
    color: var(--info-color);
    font-size: var(--font-size-sm);
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Responsive design
@media (max-width: 768px) {
  .round-finish-button-container {
    margin-top: var(--spacing-md);
    padding-top: var(--spacing-sm);
  }

  .btn-round-finish {
    width: 100%;
    justify-content: center;
  }

  .round-finish-tooltip {
    max-width: 100%;
  }
}
</style>
